import * as TweenJS from "slotworks/utils/tween";
import FreespinsWheelRecord from "integration/records/freespins-wheel-record";
import { Container, DisplayObject } from "pixi.js";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import { CenterPivot, PIXIElement, Ungroup } from "slotworks/graphics/pixi/group";
import gameState from "slotworks/model/game-state";
import FreespinRequestPayload from "slotworks/model/gameplay/requests/freespin-request-payload";
import settingsModel from "slotworks/model/settings/settings-model";
import commsManager from "slotworks/server/comms-manager";
import Contract from "slotworks/utils/contract";
import { sequence } from "slotworks/utils/race-utils";
import HarleyComponents from "components/components";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import animationManager from "slotworks/graphics/animation/animation-manager";
import soundManager from "slotworks/sound/sound-manager";
import Timer from "slotworks/utils/timer";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import deviceInfo from "slotworks/utils/device-info";

const WHEEL_SPRITES = [
    "backing_light",
    "main_part",
    "12_wedge_bg_still",
    "red2",
    "12",
    "8_wedge_bg_still",
    "red1",
    "8",
    "15_wedge_bg_still",
    "blue1",
    "15",
    "10_wedge_bg_still",
    "blue2",
    "10",
    "gold",
    "neon"
];

export default class FreespinsWheelSpins {

    public static ROTATION_TIME: number = 5500;

    private layer: Layers;
    private wheelContainer: Container;
    private wholeWheelContainer: Container;

    private hand: PIXI.Sprite;
    private handTween: TweenJS.Tween<PIXI.Sprite> = null;

    private activeAnimations: AnimatedSprite[];

    private segmentSize: number = 2 * Math.PI / 4; // 360 /sectors count

    private options: number[] = [8, 10, 12, 15];
    private selected: number; // index of options

    private sprites: DisplayObject[];
    private lastTime: number;

    private tickTimer: number;
    private spinResolve: () => void;
    private rotation: number = 0;

    private speed: number = 0;
    private maxSpeed: number = 0.01;
    private minSpeed: number = 0.0005;

    private state: number = 1; // 1 accel, 2 spin, 3 decel

    private accelTime = 1000;
    private spinTime = 2000;
    private decelTime = 5000;

    private stateTime = 0;

    private startRotation = 0;
    private targetRotation: number = 0;
    private notch: number = 0;

    constructor() {
        this.layer = Layers.Bonus;
    }

    public start() {
        if (this.shouldSkipFreespinsWheel()) {
            soundManager.customEvent("bonus_music");
            return Contract.empty();
        }
        // Layers.BetBar.hide();
        // Layers.SmartButton.hide();
        return sequence([
            () => this.zoomInWheel(),
            () => this.callToAction(),
            () => this.waitForInput(),
            () => this.spin(),
            () => this.celebrateWin(),
            // () => this.complete()
        ]);
    }

    public end() {

    }

    private shouldSkipFreespinsWheel() {
        const record = gameState.getCurrentGame().getCurrentRecord();
        return record instanceof FreespinsWheelRecord && record.spins > 0;
    }

    private waitForInput() {
        return new Contract(resolve => {
            this.selected = -1;
            const onKeyDown = (event: any) => {
                if (settingsModel.getSpaceToSpin()) {
                    if (event.keyCode === 32) {
                        onClick();
                    }
                }
            };

            const onClick = () => {
                document.removeEventListener("keyup", onKeyDown);
                document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getEventString(), onClick, true);
                this.layer.container.buttonMode = false;
                this.layer.container.interactive = false;
                TweenJS.remove(this.handTween);
                this.handTween = null;
                this.hand.visible = false;

                commsManager.request(new FreespinRequestPayload()).then(() => {
                    const record = gameState.getCurrentGame().latestRecord() as FreespinsWheelRecord;
                    const spins = record.spinsWon;
                    this.feedResult(spins);
                });
                resolve(null);
            };

            this.callToAction().then(() => {
                setTimeout(() => {
                    document.addEventListener("keyup", onKeyDown);
                    document.body.addEventListener(ButtonEvent.POINTER_DOWN.getEventString(), onClick, true);

                    this.layer.container.buttonMode = true;
                }, 100);
            });
        });
    }

    private feedResult(spins: number) {
        this.selected = this.options.indexOf(spins);
    }

    private callToAction() {
        return new Contract(resolve => {
            this.startHand();
            resolve(null);
        });
    }

    public createWheel() {
        if (this.shouldSkipFreespinsWheel()) {
            return Contract.empty();
        }
        return new Contract(resolve => {
            this.hand = this.layer.getSprite("HandIcon");
            this.hand.visible = false;

            const red = this.layer.getSprite("red1") as AnimatedSprite;
            startAnimation(red);

            const red2 = this.layer.getSprite("red2") as AnimatedSprite;
            red2.scale.y = -1;
            red2.scale.x = -1;
            red2.anchor.set(1, 1);
            startAnimation(red2);

            const blue = this.layer.getSprite("blue1") as AnimatedSprite;
            startAnimation(blue);

            const blue2 = this.layer.getSprite("blue2") as AnimatedSprite;
            blue2.scale.y = -1;
            blue2.scale.x = -1;
            blue2.anchor.set(1, 1);
            startAnimation(blue2);

            this.activeAnimations = [red, blue, red2, blue2];

            const sprites = [];
            for (let s of WHEEL_SPRITES) {
                const sprite = this.layer.getSprite(s);
                sprites.push(sprite);
            }
            this.wheelContainer = CenterPivot(sprites);
            this.sprites = this.layer.container.children.slice();

            this.wholeWheelContainer = CenterPivot(this.sprites.filter(el => el.name !== "Boundary" && el.name !== "HandIcon") as PIXIElement[], "all");
            const left = this.layer.container.children.slice();
            for (let c of left) {
                Layers.BonusForeground.add(c);
            }

            // Layers.BonusForeground.add(this.wholeWheelContainer);

            const startPosition = Layers.BonusForeground.getPosition("start");
            this.wholeWheelContainer.width = startPosition.width;
            this.wholeWheelContainer.height = startPosition.height;
            this.wholeWheelContainer.position.set(startPosition.x + startPosition.width * 0.5, startPosition.y + startPosition.height * 0.5);

            resolve(null);
        });
    }

    public zoomInWheel() {
        return new Contract(resolve => {
            setTimeout(() => soundManager.customEvent("free_spins_wheel_intro"), 800);
            const endPosition = Layers.BonusForeground.getPosition("end1");
            new TweenJS.Tween(this.wholeWheelContainer.scale)
                .to({
                    x: 1,
                    y: 1,
                },
                    600)
                .delay(800)
                .start();

            new TweenJS.Tween(this.wholeWheelContainer)
                .to({
                    x: endPosition.x + endPosition.width * 0.5,
                    y: [endPosition.y - 600, endPosition.y + endPosition.height * 0.5],
                    // width: endPosition.width,
                    // height: endPosition.height
                },
                    600)
                .delay(800)
                .interpolation(TweenJS.Interpolation.Bezier)
                // .easing(TweenJS.Easing.Quartic.Out)
                .onComplete(() => {
                    soundManager.customEvent("bonus_music");
                    // Ungroup(this.wholeWheelContainer, "all");
                    resolve(null);
                })
                .start();

            const wheel2 = Layers.BonusForeground.getSprite("wheel2");
            const scaleX = wheel2.scale.x;
            const scaleY = wheel2.scale.y;
            wheel2.anchor.set(0.5, 0.5);
            wheel2.x += wheel2.width / 2;
            wheel2.y += wheel2.height / 2;
            wheel2.scale.set(scaleX / 2, scaleY / 2);

            new TweenJS.Tween(wheel2.scale)
                .to({ x: scaleX, y: scaleY }, 600)
                .delay(800)
                .start();
        })
    }

    private clearLayer() {
        for (let c of this.sprites) {
            Layers.BonusForeground.container.removeChild(c);
        }
    }

    private spin() {
        return new Contract<void>(resolve => {
            this.spinResolve = resolve;
            HarleyComponents.harley.spin2();
            if (!deviceInfo.isDesktop && canvasManager.orientation === Orientation.PORTRAIT) {
                setTimeout(() => {
                    soundManager.customEvent("wheel_spin");
                    this.rotation = this.wheelContainer.rotation;
                    this.startSpin();
                }, 200);
            } else {
                new TweenJS.Tween(this.wheelContainer)
                    .to({ rotation: Math.PI / 4 }, 600)
                    .delay(400)
                    .onUpdate(() => this.updateNotch())
                    .onComplete(() => {
                        setTimeout(() => {
                            soundManager.customEvent("wheel_spin");
                            this.rotation = this.wheelContainer.rotation;
                            this.startSpin();
                        }, 200);
                    })
                    .start();
                }
        });
    }

    private startSpin() {
        this.rotation = this.wheelContainer.rotation;
        this.startRotation = this.rotation;

        this.state = 1;
        this.stateTime = 0;
        this.speed = this.minSpeed;

        this.startTick();
    }

    private startTick() {
        this.stopTick();
        this.tickTimer = Timer.setInterval(this.tick, 0);
        this.lastTime = Timer.time;
    }

    private stopTick() {
        Timer.clearInterval(this.tickTimer);
    }

    private tick = () => {
        this.update();
    }

    private update() {
        let difTime = Timer.time - this.lastTime;
        this.lastTime = Timer.time;
        this.stateTime += difTime;

        if (this.state === 1) {
            // ACCELERATION
            let t = this.stateTime / this.accelTime;

            if (t >= 1) {
                t = 1;
                this.state = 2;
                this.stateTime = 0;
            }

            this.speed = this.minSpeed + (this.maxSpeed - this.minSpeed) * t;
            this.rotation -= difTime * this.speed;
        } else if (this.state === 2) {
            // CONSTANT SPIN
            this.rotation -= difTime * this.maxSpeed;

            if (this.stateTime >= this.spinTime && this.selected > -1) {
                this.targetRotation =
                    this.rotation
                    - (Math.PI * 2 - Math.abs(this.rotation % (2 * Math.PI)))
                    - (Math.PI * 2 +
                        this.selected * this.segmentSize
                        + Math.PI / 4
                    );
                this.state = 3;
                this.stateTime = 0;
                this.startRotation = this.rotation;
            }
        } else if (this.state === 3) {
            // DECELERATION (EASE OUT)
            let t = this.stateTime / this.decelTime;

            if (t >= 1) {
                t = 1;
            }

            let eased = 1 - Math.pow(1 - t, 4);

            this.rotation =
                this.startRotation +
                (this.targetRotation - this.startRotation) * eased;

            if (t === 1) {
                this.stop();
            }
        }
        this.wheelContainer.rotation = this.rotation;
        this.updateNotch();
    }

    private stop() {        
        this.stopTick();
        this.spinResolve();
        this.spinResolve = null;
        soundManager.customEvent("wheel_stop");
    }

    private celebrateWin() {
        return new Contract(resolve => {
            soundManager.customEvent("free_spins_selected_wedge");
            for (let anim of this.activeAnimations) {
                anim.stop();
                anim.visible = false;
            }

            const record = gameState.getCurrentGame().latestRecord() as FreespinsWheelRecord;
            const n = record.spinsWon.toString();

            const animKeys = {
                "8": "red1",
                "10": "blue2",
                "12": "red2",
                "15": "blue1"
            }

            const anim = this.wheelContainer.getChildByName(animKeys[n as keyof typeof animKeys]) as AnimatedSprite;
            const winAnimation = animationManager.generateAnimation((n === "10" || n === "15") ? "FS_Wheel_Blue_Wedge_BG_Win" : "FS_Wheel_Red_Wedge_BG_Win");
            const sprite = this.wheelContainer.getChildByName(n);
            const index = this.wheelContainer.getChildIndex(sprite);

            this.wheelContainer.addChildAt(winAnimation, index - 1);

            const container = CenterPivot(sprite as PIXIElement);

            let scaleTween = new TweenJS.Tween(container.scale)
                .to({ x: 1.3, y: 1.4 }, 200)
                .delay(100)
                .easing(TweenJS.Easing.Back.Out)
                .onComplete(() => {
                    scaleTween = new TweenJS.Tween(container.scale)
                        .to({ x: 1, y: 1 }, 280)
                        .delay(90)
                        .start();
                })
                .start();

            winAnimation.anchor.set(anim.anchor.x, anim.anchor.y);
            winAnimation.width = anim.width;
            winAnimation.height = anim.height;
            winAnimation.position.set(anim.x, anim.y);
            winAnimation.scale.set(anim.scale.x, anim.scale.y);
            winAnimation.loop = true;
            winAnimation.gotoAndPlay(0);
            setTimeout(() => {
                if (scaleTween) {
                    scaleTween.stop();
                    TweenJS.remove(scaleTween);
                }
                winAnimation.stop();
                resolve(null);
            }, 1800);
        });
    }

    public complete() {
        if (!this.wholeWheelContainer)
            return Contract.empty();
        return new Contract(resolve => {

            const parent = this.wholeWheelContainer.parent;
            parent.removeChild(this.wholeWheelContainer);
            parent.addChild(this.wholeWheelContainer);

            new TweenJS.Tween(this.wholeWheelContainer)
                .to({ y: this.wholeWheelContainer.y + 800 }, 500)
                .delay(200)
                .onComplete(() => {
                    Ungroup(this.wholeWheelContainer, "all");
                    this.clearLayer();
                })
                .start();
            resolve(null);
        });
    }

    private updateNotch() {
        const current = Math.floor(this.wheelContainer.angle / 90);
        if(this.notch != current) {
            soundManager.customEvent("wheel_spin_notch");
        }
        this.notch = current;
    }

    private startHand = () => {
        if (this.handTween) {
            this.handTween.stop();
            TweenJS.remove(this.handTween);
        }
        const start = this.layer.localToLocal(Layers.BonusForeground, this.layer.getPosition("HandIcon2"));
        const end = this.layer.localToLocal(Layers.BonusForeground, this.layer.getPosition("HandIcon"));
        // this.hand = this.layer.getSprite("HandIcon");
        this.hand.visible = true;
        this.hand.position.set(start.x, start.y);
        this.handTween = new TweenJS.Tween(this.hand)
            .to({ x: end.x, y: end.y }, 800)
            .yoyo(true)
            .repeat(Infinity)
            .start();
    }
}

export function startAnimation(s: AnimatedSprite) {
    s.loop = true;
    s.gotoAndPlay(0);
}